import { Box } from "@mantine/core";
import DocumentPreview from "components/ImageUploader/DocumentPreview";
import { useEffect, useRef, useState } from "react";
import { DocumentWithId } from "types/document";
import { DesktopImageViewer } from "./DesktopImageViewer";

interface SwipeableImageViewerProps {
  documents: DocumentWithId[];
  selectedIndex: number | null;
  onSelectedIndexChange: (index: number | null) => void;
  isDesktop: boolean;
  messageText?: string;
}

const SWIPE_THRESHOLD = 50;
const ANIMATION_DURATION = 280;
const DESKTOP_SLIDE_WIDTH = 1200;

export const SwipeableImageViewer: React.FC<SwipeableImageViewerProps> = ({
  documents,
  selectedIndex,
  onSelectedIndexChange,
  isDesktop,
  messageText,
}) => {
  const tileSize = Math.floor((window.innerWidth - 20 - 10) / 2);
  const [slideOffset, setSlideOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const slideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const thumbnailsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (slideTimer.current !== null) clearTimeout(slideTimer.current);
    };
  }, []);

  useEffect(() => {
    if (selectedIndex === null || !thumbnailsRef.current) return;
    const thumb = thumbnailsRef.current.children[selectedIndex] as
      | HTMLElement
      | undefined;
    thumb?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedIndex]);

  const isAtStart = selectedIndex === 0;
  const isAtEnd = selectedIndex === documents.length - 1;
  const canSwipe = selectedIndex !== null && documents.length > 1;

  const slideTo = (index: number, toX: number) => {
    if (slideTimer.current !== null) clearTimeout(slideTimer.current);
    setIsAnimating(true);
    setSlideOffset(toX);
    slideTimer.current = setTimeout(() => {
      setIsAnimating(false);
      setSlideOffset(0);
      onSelectedIndexChange(index);
    }, ANIMATION_DURATION);
  };

  const snapBack = () => {
    if (slideTimer.current !== null) clearTimeout(slideTimer.current);
    setIsAnimating(true);
    setSlideOffset(0);
    slideTimer.current = setTimeout(
      () => setIsAnimating(false),
      ANIMATION_DURATION
    );
  };

  const getSlideWidth = () =>
    isDesktop ? DESKTOP_SLIDE_WIDTH : window.innerWidth;

  const step = (direction: 1 | -1) => {
    if (selectedIndex === null || isAnimating) return;
    const next = selectedIndex + direction;
    if (next < 0 || next >= documents.length) return;
    slideTo(next, direction * -getSlideWidth());
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!canSwipe) return;
    touchStartX.current = e.touches[0].clientX;
    setIsAnimating(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.touches[0].clientX - touchStartX.current;
    const atBoundary = (delta > 0 && isAtStart) || (delta < 0 && isAtEnd);
    setSlideOffset(atBoundary ? delta / 4 : delta);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || selectedIndex === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    const atBoundary = (delta > 0 && isAtStart) || (delta < 0 && isAtEnd);
    if (!atBoundary && Math.abs(delta) >= SWIPE_THRESHOLD) {
      slideTo(
        selectedIndex + (delta < 0 ? 1 : -1),
        delta < 0 ? -getSlideWidth() : getSlideWidth()
      );
    } else {
      snapBack();
    }
  };

  const buildSlide =
    (width: number | string) => (doc: (typeof documents)[number], i: number) =>
      (
        <Box
          key={doc.id ?? i}
          style={{ width, flexShrink: 0, height: "100%", position: "relative" }}
        >
          <Box style={{ position: "absolute", inset: 0 }}>
            <DocumentPreview
              isRounded={false}
              crop="contain"
              removable={false}
              document={doc.blob}
              isPdf={doc.blob.type === "application/pdf"}
              borderSize={0}
            />
          </Box>
        </Box>
      );

  if (selectedIndex !== null) {
    if (isDesktop) {
      const desktopSlides = (
        <Box
          style={{
            display: "flex",
            height: "100%",
            transform: `translateX(${
              -selectedIndex * DESKTOP_SLIDE_WIDTH + slideOffset
            }px)`,
            transition: isAnimating
              ? `transform ${ANIMATION_DURATION}ms ease`
              : "none",
            willChange: "transform",
          }}
        >
          {documents.map(buildSlide(DESKTOP_SLIDE_WIDTH))}
        </Box>
      );
      return (
        <DesktopImageViewer
          slides={desktopSlides}
          slideWidth={DESKTOP_SLIDE_WIDTH}
          documents={documents}
          selectedIndex={selectedIndex}
          onSelectedIndexChange={onSelectedIndexChange}
          isAtStart={isAtStart}
          isAtEnd={isAtEnd}
          onStep={step}
          messageText={messageText}
          thumbnailsRef={thumbnailsRef}
        />
      );
    }

    const mobileSlides = (
      <Box
        style={{
          display: "flex",
          height: "100%",
          transform: `translateX(calc(${-selectedIndex} * 100vw + ${slideOffset}px))`,
          transition: isAnimating
            ? `transform ${ANIMATION_DURATION}ms ease`
            : "none",
          touchAction: "none",
          willChange: "transform",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {documents.map(buildSlide("100vw"))}
      </Box>
    );

    return (
      <Box style={{ height: "100%", overflow: "hidden", position: "relative" }}>
        {mobileSlides}
      </Box>
    );
  }

  return (
    <Box style={{ height: "100%", overflowY: "auto", padding: 10 }}>
      <Box className="grid grid-cols-2" style={{ gap: 10 }}>
        {documents.map((doc, index) => (
          <DocumentPreview
            key={doc.id ?? index}
            width={tileSize}
            height={tileSize}
            crop="cover"
            removable={false}
            onClick={() => onSelectedIndexChange(index)}
            document={doc.blob}
            isPdf={doc.blob.type === "application/pdf"}
            borderColor="var(--color-white)"
            borderSize={5}
          />
        ))}
      </Box>
    </Box>
  );
};
